import { errorSchema } from "@app/shared";
import { ApiError } from "../lib/api";
import {
  type FichierRendu,
  type FigureSerreLivres,
  type Jeu,
  nomAfficheNormalise,
  nomAfficheValide,
  SERRE_LIVRES_ENVOI_OCTETS_MAX,
  type SerreLivresReponse,
  type SerreLivresRequete,
  serreLivresReponseSchema,
} from "./contract";

/**
 * Les jeux complets du mode serre-livres (#337), sans React : le nom d'une figurine tel qu'on le
 * tape, les plateaux que l'assemblage nommera, la requete et ce qui la rend identique a une
 * autre, son envoi, le fichier de chaque imprimante dans la reponse, et la nature d'un echec.
 * La page (`silhouettes-serre-livres-assemblage.tsx`) n'y ajoute que de l'etat et du texte.
 */

/** `GET` : le catalogue des teintes Bambu, demande a l'entree dans le mode. */
export const ROUTE_BAMBU = "/api/silhouettes/bambu";

/** `POST` : deux 3MF de figurines rendues, assembles en un jeu par imprimante. */
export const ROUTE_SERRE_LIVRES = "/api/silhouettes/serre-livres";

/** Les deux imprimantes du jeu, dans l'ordre de la reponse : la P1S, puis l'A1 mini. */
export const IMPRIMANTES: readonly Imprimante[] = ["p1s", "a1mini"];

export type Imprimante = Jeu["imprimante"];

/* -------------------------------------------------------------------------------------------
 * Le nom
 * ----------------------------------------------------------------------------------------- */

/**
 * L'apostrophe typographique, ouvrante ou fermante : la saisie automatique d'un telephone la tape,
 * et l'outil ne connait que l'apostrophe droite.
 */
const APOSTROPHES_TYPOGRAPHIQUES = /[‘’]/g;

/** Ce que la saisie du nom donne : rien, un nom refuse, ou le nom tel qu'il part. */
export type NomLu = { etat: "vide" } | { etat: "invalide" } | { etat: "valide"; nom: string };

/**
 * Lit la saisie du champ Nom. L'apostrophe typographique devient l'apostrophe droite, les espaces
 * du bord tombent (la regle de l'outil les refuse, et son message ne les dirait pas), puis le nom
 * est juge par `nomAfficheValide` et part sous sa forme NFC (`nomAfficheNormalise`), la seule que
 * le contrat accepte.
 */
export function lireNom(saisie: string): NomLu {
  const propre = saisie.replace(APOSTROPHES_TYPOGRAPHIQUES, "'").trim();
  if (propre === "") return { etat: "vide" };
  if (!nomAfficheValide(propre)) return { etat: "invalide" };
  return { etat: "valide", nom: nomAfficheNormalise(propre) };
}

/* -------------------------------------------------------------------------------------------
 * Les plateaux, avant que l'assemblage ne les rende
 * ----------------------------------------------------------------------------------------- */

/** Ce que le nom d'un plateau de figurine demande : le nom tel qu'il part, et ses filaments. */
export type PlateauDeFigurine = { nom: string; filaments: number };

/**
 * Les plateaux d'un jeu, avant tout assemblage. Le contrat ne les rend que dans la reponse, et la
 * page les montre sous chaque bouton des les deux rendus faits : elle les calcule donc par la regle
 * de `products`, que la reponse remplace ensuite. La source : `assemblage.py`
 * (`packages/products/silhouette/silhouette/serre_livres/`), `_ecrire_jeu` pour le nom d'une
 * figurine (« Charmander - 4 colors », « Squirtle - 1 color » : `color` au singulier pour un seul
 * filament) et `PLATEAUX_DE_LA_BASE` pour ce qui suit.
 *
 * - P1S : la figurine de gauche, celle de droite, « Bookends x2 », « Display stands x2 » ;
 * - A1 mini : les deux figurines, « Display stands x2 », « Bookend », « Bookend » (le plateau fait
 *   180 mm : un Bookend par plateau).
 *
 * `filaments` est celui de la figurine apres les teintes choisies (deux couleurs ramenees a la
 * meme teinte n'en font qu'un) : `filamentsDeLaFigure`, de `silhouettes-filaments`.
 */
export function plateauxAvantAssemblage(
  imprimante: Imprimante,
  gauche: PlateauDeFigurine,
  droite: PlateauDeFigurine,
): string[] {
  const figurines = [gauche, droite].map(
    ({ nom, filaments }) => `${nom} - ${filaments} color${filaments > 1 ? "s" : ""}`,
  );
  return imprimante === "p1s"
    ? [...figurines, "Bookends x2", "Display stands x2"]
    : [...figurines, "Display stands x2", "Bookend", "Bookend"];
}

/* -------------------------------------------------------------------------------------------
 * La requete
 * ----------------------------------------------------------------------------------------- */

/**
 * La taille de ce que la page envoie, au sens du contrat : les deux 3MF ensemble, en caracteres de
 * base64. Le frontal nginx du template refuse un corps de plus de 2 Mo par une page HTML, que la
 * console ne verrait jamais : c'est a la page de s'arreter avant l'envoi.
 */
export function octetsDEnvoi(requete: SerreLivresRequete): number {
  return requete.gauche.fichier_3mf.length + requete.droite.fichier_3mf.length;
}

/** Vrai quand l'envoi depasse `SERRE_LIVRES_ENVOI_OCTETS_MAX` : la page ne l'envoie pas. */
export function envoiTropLourd(requete: SerreLivresRequete): boolean {
  return octetsDEnvoi(requete) > SERRE_LIVRES_ENVOI_OCTETS_MAX;
}

function memeTeintes(a: Record<string, string>, b: Record<string, string>): boolean {
  const cles = Object.keys(a);
  return cles.length === Object.keys(b).length && cles.every((cle) => a[cle] === b[cle]);
}

function memeFigure(a: FigureSerreLivres, b: FigureSerreLivres): boolean {
  return (
    a.fichier_3mf === b.fichier_3mf &&
    a.nom === b.nom &&
    a.miroir === b.miroir &&
    memeTeintes(a.teintes, b.teintes)
  );
}

/**
 * Deux requetes disent-elles la meme chose ? C'est la cle de la reponse gardee : tant que les deux
 * 3MF, les noms, les miroirs et les teintes (celles des figurines et celle des Bookends et des
 * Socles) ne changent pas, un second jeu se telecharge de la reponse deja la, sans nouveau calcul.
 * Les 3MF se comparent comme des textes : le meme texte, d'une meme reponse, l'est en un coup.
 */
export function memeRequete(a: SerreLivresRequete, b: SerreLivresRequete): boolean {
  return (
    a.teinte_base === b.teinte_base &&
    memeFigure(a.gauche, b.gauche) &&
    memeFigure(a.droite, b.droite)
  );
}

/**
 * `POST /api/silhouettes/serre-livres`, comme `envoyerRendu` fait le sien : une `ApiError` avec le
 * code et le message du backend, `http_error` et le statut quand le corps n'est pas du JSON (une
 * coupure du frontal nginx), `contract_broken` pour une reponse hors contrat. Un echec reseau
 * n'est pas une `ApiError` : c'est a l'appelant d'en faire une.
 */
export async function envoyerAssemblage(
  requete: SerreLivresRequete,
  signal?: AbortSignal,
): Promise<SerreLivresReponse> {
  const response = await fetch(ROUTE_SERRE_LIVRES, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(requete),
    ...(signal ? { signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "http_error",
      parsed.success ? parsed.data.error.message : `Erreur HTTP ${response.status}`,
      response.status,
    );
  }

  const result = serreLivresReponseSchema.safeParse(payload);
  if (!result.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return result.data;
}

/* -------------------------------------------------------------------------------------------
 * La reponse
 * ----------------------------------------------------------------------------------------- */

/** Le jeu d'une imprimante dans la reponse. */
export function jeuDe(reponse: SerreLivresReponse, imprimante: Imprimante): Jeu | undefined {
  return reponse.jeux.find((jeu) => jeu.imprimante === imprimante);
}

/**
 * Le fichier d'une imprimante. `fichiers` n'a qu'un 3MF par jeu que l'imprimante accepte, dans
 * l'ordre des jeux : un jeu refuse (`refus`) n'a pas de fichier, et le suivant prend sa place. Le
 * rang se lit donc en comptant les jeux acceptes, pas en lisant le nom du fichier.
 */
export function fichierDuJeu(
  reponse: SerreLivresReponse,
  imprimante: Imprimante,
): FichierRendu | null {
  let rang = 0;
  for (const jeu of reponse.jeux) {
    if (jeu.refus !== null) continue;
    if (jeu.imprimante === imprimante) return reponse.fichiers[rang] ?? null;
    rang += 1;
  }
  return null;
}

/* -------------------------------------------------------------------------------------------
 * L'echec
 * ----------------------------------------------------------------------------------------- */

/**
 * Ce qui a fait echouer un assemblage, du point de vue de la page (spec, section 6) :
 *
 * | Reponse | Genre |
 * |---|---|
 * | 413, ou l'envoi trop lourd avant meme de partir | `trop-lourd` |
 * | 400 `rendu_refuse`, « 3MF de figurine illisible... » | `illisible` : le rendu est a refaire |
 * | 400 `rendu_refuse`, « assemblage interrompu apres 20 s » | `interrompu` |
 * | 400 `rendu_refuse`, tout autre refus de l'outil | `refuse`, son message |
 * | 400 d'une validation de la console | `invalide` |
 * | 429 | `occupe` |
 * | 502 `calcul_indisponible` (reglages d'imprimante absents de l'image `products`) | `indisponible` |
 * | 502 `calcul_en_panne` | `en-panne` |
 * | 504, `http_error` 504 compris | `ne-repond-pas` |
 * | le reste : 502, reseau coupe, reponse hors contrat | `injoignable` |
 *
 * Les deux 400 de l'outil ne se distinguent que par leur message (meme code, meme statut).
 */
export type Echec =
  | { genre: "trop-lourd" }
  | { genre: "illisible" }
  | { genre: "interrompu" }
  | { genre: "refuse"; message: string }
  | { genre: "invalide" }
  | { genre: "occupe" }
  | { genre: "indisponible" }
  | { genre: "en-panne" }
  | { genre: "ne-repond-pas" }
  | { genre: "injoignable" };

export function classerEchec(erreur: unknown): Echec {
  if (!(erreur instanceof ApiError)) return { genre: "injoignable" };
  if (erreur.status === 413) return { genre: "trop-lourd" };
  if (erreur.status === 400) {
    if (erreur.code !== "rendu_refuse") return { genre: "invalide" };
    // Les phrases entieres de `figure.py` et d'`isolement.py`, pas le mot : une piece de 3MF peut
    // porter n'importe quel nom, et `service.py` le cite (« gauche : couleur sans teinte : ... »).
    if (/3MF de figurine illisible/.test(erreur.message)) return { genre: "illisible" };
    if (/assemblage interrompu/.test(erreur.message)) return { genre: "interrompu" };
    return { genre: "refuse", message: erreur.message };
  }
  if (erreur.status === 429) return { genre: "occupe" };
  if (erreur.status === 504) return { genre: "ne-repond-pas" };
  if (erreur.code === "calcul_indisponible") return { genre: "indisponible" };
  if (erreur.code === "calcul_en_panne") return { genre: "en-panne" };
  return { genre: "injoignable" };
}
